import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import type { Report } from '@muzly/voice-engine';

import {
  INITIAL_ONBOARDING_STATE,
  currentStepId,
  isFirstStep,
  isLastStep,
  onboardingReducer,
  stepProgress,
} from '../flow';
import { draftStore } from '../storage';

import type { CaptureSource } from '../capture';
import type { FlowConfig, OnboardingAnswer, OnboardingStepId } from '../flow';
import type { DiagnosisRecord } from '../record';

/**
 * 단계형 플로우(초기 설정·진단) 상태를 하나로 모으는 컨텍스트.
 *
 * 순수 머신(onboardingReducer)을 **플로우 설정**으로 한 번 구동하고, 화면들이 공유하는
 * 파생값(현재 단계·진행도·처음/마지막)과 액션(next/back/answer/reset)만 얹는다.
 * 진단 리포트(report)는 문자열 answers 맵에 안 맞는 구조체라 별도 슬롯으로 둔다 —
 * 녹음이 채우고 결과가 읽는다.
 */

interface OnboardingValue {
  /** 이 프로바이더가 도는 플로우 — 셸이 닫기 버튼 유무를 여기서 읽는다. */
  flow: FlowConfig;
  step: OnboardingStepId;
  progress: { current: number; total: number };
  isFirst: boolean;
  isLast: boolean;
  /** 밑에 깐 답(프로필) 위에 이 플로우의 답을 얹은 것 — 엔진·저장이 읽는 하나의 맵. */
  answers: Record<string, OnboardingAnswer>;
  /** 음성 진단 리포트 — 녹음 완료 전이거나 무효 음성이면 null. */
  report: Report | null;
  /**
   * 그 리포트가 어느 전송에서 나왔나. 브라우저 mock이면 실측이 아니므로 결과 화면이
   * 그 사실을 표시한다. 리포트와 **같이** 실려야 한다 — 결과 화면에서
   * `isRunningInNativeApp()`을 다시 물으면 복원된 드래프트의 출처를 잘못 말하게 된다.
   */
  reportSource: CaptureSource | null;
  /**
   * 그 회차로 **저장한 것**(리포트의 투영 + 노래 채점 + 성구 전환). 결과 화면이 그리는 값의
   * 정본이다 — 홈·이력이 읽는 것과 같은 모양이라 세 화면이 안 갈린다.
   *
   * 드래프트에 안 남긴다: 결과 화면은 그 세션 안에서만 열리고, 저장은 이미
   * `diagnosisStore`가 소유한다. 두 군데 남기면 "마지막 진단"의 진실이 둘이 된다.
   */
  record: DiagnosisRecord | null;
  /** 다음 단계로. 마지막 단계에서는 플로우를 나간다(`onComplete`). */
  next: () => void;
  back: () => void;
  answer: (id: string, value: OnboardingAnswer) => void;
  /**
   * 리포트·출처·회차를 **한 번에** 싣는다. 세터를 나누면 리포트만 들어온 렌더가 한 번
   * 생겨 결과 화면이 "분석 실패"를 깜빡인다.
   */
  setReport: (
    report: Report | null,
    source?: CaptureSource | null,
    record?: DiagnosisRecord | null,
  ) => void;
}

const OnboardingContext = createContext<OnboardingValue | null>(null);

export const OnboardingProvider = ({
  flow,
  initialAnswers,
  onComplete,
  children,
}: {
  flow: FlowConfig;
  /**
   * 드래프트 **밑에** 까는 답 — 진단 플로우가 프로필(성별·나이…)을 여기로 받는다.
   * 드래프트에 같은 키가 있으면 드래프트가 이긴다(이 회차에서 고친 값). **드래프트에는
   * 안 쓴다** — 프로필 사본이 드래프트에 남으면 나중에 프로필을 고쳐도 사본이 이긴다.
   */
  initialAnswers?: Record<string, OnboardingAnswer>;
  /**
   * 마지막 단계에서 `next()`가 할 일 — 플로우를 나가는 쪽(페이지)이 정한다. 리듀서는 끝에서
   * 멈추므로 여기 없으면 마지막 CTA는 아무 일도 안 한다.
   */
  onComplete?: (answers: Record<string, OnboardingAnswer>) => void;
  children: ReactNode;
}) => {
  const store = useMemo(() => draftStore(flow), [flow]);
  // 드래프트는 마운트 때 한 번만 읽는다 — 세 초기값(위치·답, 리포트, 출처)이 같은 읽기를 나눠 쓴다.
  const [draft] = useState(() => store.load());
  const [state, dispatch] = useReducer(
    onboardingReducer(flow),
    INITIAL_ONBOARDING_STATE,
    (initial) =>
      draft ? { index: draft.index, answers: draft.answers } : initial,
  );
  const [report, setReportState] = useState<Report | null>(
    () => draft?.report ?? null,
  );
  const [reportSource, setReportSource] = useState<CaptureSource | null>(
    () => draft?.reportSource ?? null,
  );
  const [record, setRecordState] = useState<DiagnosisRecord | null>(null);
  const setReport = useCallback(
    (
      next: Report | null,
      source: CaptureSource | null = null,
      nextRecord: DiagnosisRecord | null = null,
    ): void => {
      setReportState(next);
      setReportSource(next ? source : null);
      setRecordState(next ? nextRecord : null);
    },
    [],
  );

  // Persist the live draft on every change (position, this flow's answers, report).
  useEffect(() => {
    store.save({
      index: state.index,
      answers: state.answers,
      report,
      reportSource,
    });
  }, [store, state, report, reportSource]);

  const value = useMemo<OnboardingValue>(() => {
    const isLast = isLastStep(flow, state);
    const answers = { ...(initialAnswers ?? {}), ...state.answers };
    return {
      flow,
      step: currentStepId(flow, state),
      progress: stepProgress(flow, state),
      isFirst: isFirstStep(state),
      isLast,
      answers,
      report,
      reportSource,
      record,
      next: () => {
        if (isLast) {
          onComplete?.(answers);
        } else {
          dispatch({ type: 'next' });
        }
      },
      back: () => dispatch({ type: 'back' }),
      answer: (id, value) => dispatch({ type: 'answer', id, value }),
      setReport,
    };
  }, [
    flow,
    state,
    initialAnswers,
    onComplete,
    report,
    reportSource,
    record,
    setReport,
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
};
